import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Spinner, 
  Alert,
  InputGroup
} from 'react-bootstrap';
import { 
  FaSave, 
  FaBuilding, 
  FaCog, 
  FaMapMarkerAlt, 
  FaIdCard, 
  FaPhone, 
  FaEnvelope, 
  FaClock, 
  FaCalendarAlt,
  FaPlus,
  FaMinus
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../context/ThemeContext';
import { useAlert } from '../context/AlertContext';
import configService from '../services/configService';

/** Valore configurator (numero, "21,30", "21:30", …) → "HH:MM" per il selettore UI */
const convertConfiguratorOrarioToHHMM = (raw, defaultTime = '15:00') => {
  if (raw === null || raw === undefined || raw === '') return defaultTime;

  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    const ore = Math.floor(raw);
    const parteDecimale = raw - ore;
    const minuti = parteDecimale > 0 ? 30 : 0;
    return `${ore.toString().padStart(2, '0')}:${minuti.toString().padStart(2, '0')}`;
  }

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return defaultTime;
    if (s.includes(',')) return s.replace(',', ':');
    if (s.includes(':')) return s;
    return `${s.padStart(2, '0')}:00`;
  }

  return defaultTime;
};

const getOrarioProduzioneLabel = (orarioProdPane) => {
  const raw = String(orarioProdPane ?? '').trim();
  const [hourPart] = raw.split(':');
  const hour = Number.parseInt(hourPart, 10);
  if (Number.isFinite(hour) && hour > 7) return 'Orario avvio produzione di oggi';
  return 'Orario avvio produzione di domani';
};

const AdminSettings = () => {
  const { darkMode } = useContext(ThemeContext);
  const { showAlertGlobal } = useAlert();
  
  // Stati per i dati del form
  const [formData, setFormData] = useState({
    NOMEDIT: '',
    INDDIT: '',
    PRODIT: '',
    PIVADIT: '',
    CODFISC: '',
    CITDIT: '',
    CAPDIT: '',
    TELDIT: '',
    EMAIL_DITTA: '',
    CHIUSURADITTA1: '',
    CHIUSURADITTA2: '',
    ORARIOPRODPANE: '',
    ORARIOBLOCCO: '',
    ORARIOSBLOCCO: ''
  });

  // Stati per il caricamento e gli errori
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Stati per il logo
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  // Carica i dati al mount del componente
  useEffect(() => {
    loadConfigData();
    loadLogo();
  }, []);

  // Funzione per caricare i dati dalla configurazione
  const loadConfigData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📋 Caricamento dati configurazione amministratore...');
      const response = await configService.loadAllConfigurators();
      
      if (response && response.data) {
        const data = response.data;
        
        // Estrae solo i campi che ci interessano
        const orarioProd = convertConfiguratorOrarioToHHMM(data.ORARIOPRODPANE, '15:00');
        const orarioBlocco = convertConfiguratorOrarioToHHMM(data.ORARIOBLOCCO, '22:00');
        const orarioSblocco = convertConfiguratorOrarioToHHMM(data.ORARIOSBLOCCO, '08:00');

        const configData = {
          NOMEDIT: data.NOMEDIT || '',
          INDDIT: data.INDDIT || '',
          PRODIT: data.PRODIT || '',
          PIVADIT: data.PIVADIT || '',
          CODFISC: data.CODFISC || '',
          CITDIT: data.CITDIT || '',
          CAPDIT: data.CAPDIT || '',
          TELDIT: data.TELDIT || '',
          EMAIL_DITTA: data.EMAIL_DITTA || '',
          CHIUSURADITTA1: data.CHIUSURADITTA1 || '',
          CHIUSURADITTA2: data.CHIUSURADITTA2 || '',
          ORARIOPRODPANE: orarioProd,
          ORARIOBLOCCO: orarioBlocco,
          ORARIOSBLOCCO: orarioSblocco
        };
        
        setFormData(configData);
        console.log('✅ Dati configurazione caricati:', configData);
      } else {
        throw new Error('Risposta non valida dal server');
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento dei dati:', error);
      setError(`Errore nel caricamento: ${error.message}`);
      showAlertGlobal('Errore nel caricamento dei dati di configurazione', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Carica il logo aziendale
  const loadLogo = async () => {
    try {
      setLogoLoading(true);
      const logoBase64 = await configService.getLogo();
      if (logoBase64) {
        // Converte la stringa base64 in data URL
        let logoDataUrl;
        if (logoBase64.startsWith('data:image/')) {
          logoDataUrl = logoBase64;
        } else {
          // Aggiungi il prefisso data URL se mancante
          logoDataUrl = `data:image/png;base64,${logoBase64}`;
        }
        setLogoUrl(logoDataUrl);
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento del logo:', error);
    } finally {
      setLogoLoading(false);
    }
  };

  // Gestisce la selezione del file logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verifica che sia un'immagine
      if (!file.type.startsWith('image/')) {
        showAlertGlobal('Seleziona un file immagine valido (PNG, JPG, GIF)', 'danger');
        return;
      }
      
      // Verifica dimensione (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showAlertGlobal('Il file deve essere inferiore a 2MB', 'danger');
        return;
      }
      
      setLogoFile(file);
      
      // Anteprima del nuovo logo
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Salva il logo
  const saveLogo = async () => {
    if (!logoFile) return;
    
    try {
      setLogoLoading(true);
      
      // Converti il file in base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          let base64String = event.target.result;
          
          // Rimuovi il prefisso data URL se presente
          if (base64String.includes(',')) {
            base64String = base64String.split(',')[1];
          }
          
          console.log('💾 Salvataggio logo in corso...');
          await configService.setLogo(base64String);
          
          console.log('✅ Logo salvato con successo');
          showAlertGlobal('Logo aziendale aggiornato con successo!', 'success');
          
          // Reset del file selezionato
          setLogoFile(null);
          
        } catch (error) {
          console.error('❌ Errore nel salvataggio del logo:', error);
          showAlertGlobal(`Errore nel salvataggio del logo: ${error.message}`, 'danger');
          
          // Ricarica il logo originale in caso di errore
          loadLogo();
        } finally {
          setLogoLoading(false);
        }
      };
      
      reader.readAsDataURL(logoFile);
    } catch (error) {
      console.error('❌ Errore nella lettura del file:', error);
      showAlertGlobal('Errore nella lettura del file', 'danger');
      setLogoLoading(false);
    }
  };

  // Gestisce i cambiamenti negli input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Per i campi data, convertiamo da ISO a formato dd/mm/yyyy per il salvataggio
    if (name === 'CHIUSURADITTA1' || name === 'CHIUSURADITTA2') {
      const formattedValue = dateFromISO(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Funzioni per convertire le date tra formato dd/mm/yyyy e ISO
  const dateToISO = (dateString) => {
    if (!dateString) return '';
    
    // Se è già in formato ISO (yyyy-mm-dd), restituisce così com'è
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    
    // Converte da dd/mm/yyyy a yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return '';
  };

  const dateFromISO = (isoString) => {
    if (!isoString) return '';
    
    // Converte da yyyy-mm-dd a dd/mm/yyyy
    const parts = isoString.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    
    return isoString;
  };

  // Funzioni per gestire l'orario produzione
  const timeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return 900; // Default 15:00 = 900 minuti
    
    // Se è un numero, assumiamo che sia già in ore (es. 21 = 21:00)
    if (!isNaN(timeString)) {
      return parseInt(timeString) * 60;
    }
    
    // Se contiene ':', lo splittiamo
    if (timeString.includes(':')) {
      const [hours, minutes] = timeString.split(':').map(Number);
      return (hours * 60) + (minutes || 0);
    }
    
    // Fallback: assumiamo sia ore intere
    return parseInt(timeString) * 60 || 900;
  };

  const minutesToTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const adjustTime = (fieldName, increment) => {
    const currentTime = formData[fieldName] || '15:00';
    let totalMinutes = timeToMinutes(currentTime);

    totalMinutes += increment;

    if (totalMinutes > 23 * 60 + 30) {
      totalMinutes = 0;
    } else if (totalMinutes < 0) {
      totalMinutes = 23 * 60 + 30;
    }

    const newTime = minutesToTime(totalMinutes);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: newTime
    }));
  };

  const hhmmToConfiguratorComma = (hhmm) =>
    hhmm && typeof hhmm === 'string' ? hhmm.replace(':', ',') : '';

  // Gestisce il salvataggio dei dati
  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const dataToSave = {
        ...formData,
        ORARIOPRODPANE: hhmmToConfiguratorComma(formData.ORARIOPRODPANE),
        ORARIOBLOCCO: hhmmToConfiguratorComma(formData.ORARIOBLOCCO),
        ORARIOSBLOCCO: hhmmToConfiguratorComma(formData.ORARIOSBLOCCO)
      };
      
      console.log('💾 Salvataggio dati amministratore...', dataToSave);
      
      // Chiamata al servizio per aggiornare la configurazione
      await configService.updateMultiConfigurator(dataToSave);
      
      console.log('✅ Dati salvati con successo');
      showAlertGlobal('Dati amministratore salvati con successo!', 'success');
      
      // Ricarica i dati per confermare il salvataggio
      await loadConfigData();
      
    } catch (error) {
      console.error('❌ Errore nel salvataggio:', error);
      showAlertGlobal(`Errore nel salvataggio: ${error.message}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Funzione per formattare l'orario
  const formatTime = (value) => {
    if (!value) return '';
    const numValue = parseInt(value);
    if (isNaN(numValue)) return value;
    
    const hours = Math.floor(numValue);
    const minutes = (numValue - hours) * 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
        <Navbar />
        <Container fluid className="mt-5 pt-3">
          <Row className="justify-content-center">
            <Col xs={12}>
              <div className="text-center">
                <Spinner animation="border" variant={darkMode ? "light" : "primary"} />
                <p className={`mt-2 ${darkMode ? 'text-light' : 'text-dark'}`}>Caricamento dati amministratore...</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <Navbar />
      <Container fluid className="mt-5 pt-3">
        <Row className="justify-content-center">
          <Col xs={12}>
            {/* Header con stesso stile di altre pagine */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className={darkMode ? 'text-light' : 'text-dark'}>
                  <FaCog className="me-2" />
                  Impostazioni Amministratore
                </h2>
                <p className={`${darkMode ? 'text-muted' : 'text-muted'} mb-0`}>
                  Gestisci i dati aziendali e le configurazioni del sistema
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
                size="lg"
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Salva Modifiche
                  </>
                )}
              </Button>
            </div>

            {/* Alert errore */}
            {error && (
              <Alert 
                variant="danger" 
                className={`mb-4 ${darkMode ? 'alert-dark border-danger' : ''}`}
              >
                <strong>Errore:</strong> {error}
              </Alert>
            )}

            <Card onSubmit={handleSave}>
              <Row>
                {/* Dati Aziendali */}
                <Col lg={6} className="mb-4">
                  <Card className={`h-100 ${darkMode ? 'text-light' : ''}`}>
                    <Card.Header className={darkMode ? 'bg-secondary text-light' : 'bg-primary text-white'}>
                      <h5 className="mb-0">
                        <FaBuilding className="me-2" />
                        Dati Aziendali
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      {/* Nome Ditta */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaBuilding className="me-1" />
                          Ragione Sociale
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="NOMEDIT"
                          value={formData.NOMEDIT}
                          onChange={handleInputChange}
                          placeholder="Inserisci la ragione sociale"
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Indirizzo */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaMapMarkerAlt className="me-1" />
                          Indirizzo
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="INDDIT"
                          value={formData.INDDIT}
                          onChange={handleInputChange}
                          placeholder="Inserisci l'indirizzo"
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Città e Provincia */}
                      <Row>
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <Form.Label>Città</Form.Label>
                            <Form.Control
                              type="text"
                              name="CITDIT"
                              value={formData.CITDIT}
                              onChange={handleInputChange}
                              placeholder="Inserisci la città"
                              className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Provincia</Form.Label>
                            <Form.Control
                              type="text"
                              name="PRODIT"
                              value={formData.PRODIT}
                              onChange={handleInputChange}
                              placeholder="PR"
                              maxLength={2}
                              className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* CAP */}
                      <Form.Group className="mb-3">
                        <Form.Label>CAP</Form.Label>
                        <Form.Control
                          type="text"
                          name="CAPDIT"
                          value={formData.CAPDIT}
                          onChange={handleInputChange}
                          placeholder="00000"
                          maxLength={5}
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Logo Aziendale */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaBuilding className="me-1" />
                          Logo Aziendale
                        </Form.Label>
                        
                        {/* Anteprima del logo */}
                        <div className="mb-3 text-center">
                          {logoLoading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '120px' }}>
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : logoUrl ? (
                            <img 
                              src={logoUrl} 
                              alt="Logo Azienda" 
                              style={{ 
                                maxWidth: "200px", 
                                maxHeight: "120px", 
                                objectFit: "contain",
                                border: darkMode ? '1px solid #495057' : '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '10px'
                              }} 
                              className="img-fluid"
                            />
                          ) : (
                            <div 
                              className={`d-flex justify-content-center align-items-center ${darkMode ? 'bg-secondary text-light' : 'bg-light text-muted'}`}
                              style={{ 
                                height: '120px', 
                                border: darkMode ? '1px dashed #495057' : '1px dashed #dee2e6',
                                borderRadius: '4px'
                              }}
                            >
                              <div className="text-center">
                                <FaBuilding size={24} className="mb-2" />
                                <div>Nessun logo caricato</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Input per selezionare il file */}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          disabled={logoLoading}
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                        <Form.Text className="text-muted">
                          Formati supportati: PNG, JPG, GIF. Dimensione massima: 2MB
                        </Form.Text>

                        {/* Pulsante per salvare il logo */}
                        {logoFile && (
                          <div className="mt-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={saveLogo}
                              disabled={logoLoading}
                            >
                              {logoLoading ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Salvataggio...
                                </>
                              ) : (
                                <>
                                  <FaSave className="me-1" />
                                  Salva Logo
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Dati Fiscali e Contatti */}
                <Col lg={6} className="mb-4">
                  <Card className={`h-100 ${darkMode ? 'text-light' : ''}`}>
                    <Card.Header className={darkMode ? 'bg-secondary text-light' : 'bg-success text-white'}>
                      <h5 className="mb-0">
                        <FaIdCard className="me-2" />
                        Dati Fiscali e Contatti
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      {/* Partita IVA */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaIdCard className="me-1" />
                          Partita IVA
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="PIVADIT"
                          value={formData.PIVADIT}
                          onChange={handleInputChange}
                          placeholder="00000000000"
                          maxLength={11}
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Codice Fiscale */}
                      <Form.Group className="mb-3">
                        <Form.Label>Codice Fiscale</Form.Label>
                        <Form.Control
                          type="text"
                          name="CODFISC"
                          value={formData.CODFISC}
                          onChange={handleInputChange}
                          placeholder="Codice fiscale"
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Telefono */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaPhone className="me-1" />
                          Telefono
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="TELDIT"
                          value={formData.TELDIT}
                          onChange={handleInputChange}
                          placeholder="000 0000000"
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>

                      {/* Email */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaEnvelope className="me-1" />
                          Email Aziendale
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="EMAIL_DITTA"
                          value={formData.EMAIL_DITTA}
                          onChange={handleInputChange}
                          placeholder="info@azienda.it"
                          className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Configurazioni Operative */}
              <Row>
                <Col lg={12}>
                  <Card className={`${darkMode ? 'text-light' : ''}`}>
                    <Card.Header className={darkMode ? 'bg-secondary text-light' : 'bg-info text-white'}>
                      <h5 className="mb-0">
                        <FaClock className="me-2" />
                        Configurazioni Operative
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        {/* ORARIOPRODPANE */}
                        <Col md={6} lg={4}>
                          <Form.Group className="mb-3 mb-lg-0">
                            <Form.Label>
                              <FaClock className="me-1" />
                              {getOrarioProduzioneLabel(formData.ORARIOPRODPANE)}
                            </Form.Label>
                            <InputGroup>
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOPRODPANE', -30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaMinus />
                              </Button>
                              <Form.Control
                                type="text"
                                name="ORARIOPRODPANE"
                                value={formData.ORARIOPRODPANE || '15:00'}
                                readOnly
                                className={`text-center fw-bold ${darkMode ? 'bg-dark text-light border-secondary' : ''}`}
                                style={{ fontSize: '1.1rem' }}
                              />
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOPRODPANE', 30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaPlus />
                              </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                              riferimento per produzione e vincoli di modifica ordine (incrementi 30 min)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {/* ORARIOBLOCCO */}
                        <Col md={6} lg={4}>
                          <Form.Group className="mb-3 mb-lg-0">
                            <Form.Label>
                              <FaClock className="me-1" />
                              Orario interruzione ordini di Oggi
                            </Form.Label>
                            <InputGroup>
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOBLOCCO', -30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaMinus />
                              </Button>
                              <Form.Control
                                type="text"
                                name="ORARIOBLOCCO"
                                value={formData.ORARIOBLOCCO || '22:00'}
                                readOnly
                                className={`text-center fw-bold ${darkMode ? 'bg-dark text-light border-secondary' : ''}`}
                                style={{ fontSize: '1.1rem' }}
                              />
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOBLOCCO', 30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaPlus />
                              </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                              da quest’ora non si modificano ordini per il giorno successivo (incrementi 30 min)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {/* ORARIOSBLOCCO */}
                        <Col md={6} lg={4}>
                          <Form.Group className="mb-3 mb-lg-0">
                            <Form.Label>
                              <FaClock className="me-1" />
                              Orario ripresa ordini di Oggi
                            </Form.Label>
                            <InputGroup>
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOSBLOCCO', -30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaMinus />
                              </Button>
                              <Form.Control
                                type="text"
                                name="ORARIOSBLOCCO"
                                value={formData.ORARIOSBLOCCO || '08:00'}
                                readOnly
                                className={`text-center fw-bold ${darkMode ? 'bg-dark text-light border-secondary' : ''}`}
                                style={{ fontSize: '1.1rem' }}
                              />
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => adjustTime('ORARIOSBLOCCO', 30)}
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '40px' }}
                              >
                                <FaPlus />
                              </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                              riapertura modifiche (incrementi 30 min)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-0">
                        {/* Chiusura 1 */}
                        <Col md={6} lg={6}>
                          <Form.Group className="mb-3 mb-md-0">
                            <Form.Label>
                              <FaCalendarAlt className="me-1" />
                              Inizio Chiusura
                            </Form.Label>
                            <Form.Control
                              type="date"
                              name="CHIUSURADITTA1"
                              value={dateToISO(formData.CHIUSURADITTA1)}
                              onChange={handleInputChange}
                              className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                            />
                            <Form.Text className="text-muted">
                              Data inizio chiusura festiva
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {/* Chiusura 2 */}
                        <Col md={6} lg={6}>
                          <Form.Group className="mb-0">
                            <Form.Label>
                              <FaCalendarAlt className="me-1" />
                              Fine Chiusura
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="date"
                                name="CHIUSURADITTA2"
                                value={dateToISO(formData.CHIUSURADITTA2)}
                                onChange={handleInputChange}
                                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                              />
                              <Button
                                variant={darkMode ? 'outline-light' : 'outline-secondary'}
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    CHIUSURADITTA1: ' - - ',
                                    CHIUSURADITTA2: ' - - '
                                  }));
                                }}
                                title="Svuota entrambe le date di chiusura"
                                className="d-flex align-items-center"
                              >
                                <FaMinus className="me-1" />
                                Svuota
                              </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                              Data fine chiusura estiva
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminSettings;